import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useAuthState } from "@/hooks/useAuthState";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { logger } from "@/utils/logger";

interface AuthenticationCheckProps {
  children: React.ReactNode;
}

const AuthenticationCheck = ({ children }: AuthenticationCheckProps) => {
  const [profileChecked, setProfileChecked] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const { user, loading } = useAuthState();
  const location = useLocation();
  const [sessionVerified, setSessionVerified] = useState(false);
  const [checkError, setCheckError] = useState<Error | null>(null);

  const isLovableEditor = window.location.hostname.includes('lovable.dev') || 
                         window.location.hostname.includes('lovableproject.com') ||
                         window.location.hostname.includes('lovable.app');

  logger.debug('AuthenticationCheck: Loading:', loading, 'User:', user?.id, 'Location:', location.pathname);
  
  useEffect(() => {
    if (!loading && !user && !isLovableEditor && !sessionVerified) {
      logger.debug('No user found, verifying session directly with Supabase');
      
      const verifySession = async () => {
        try {
          const { data } = await supabase.auth.getSession();
          logger.debug('Direct session check result:', data.session ? 'Has session' : 'No session');
          
          if (data.session?.user) {
            logger.debug('Session found but user state is null, refreshing page');
            window.location.reload();
            return;
          }
          
          setSessionVerified(true);
        } catch (error) {
          console.error('Error verifying session:', error);
          setCheckError(error instanceof Error ? error : new Error('Session verification failed'));
          setSessionVerified(true);
        }
      };
      
      verifySession();
    }
  }, [loading, user, isLovableEditor, sessionVerified]);
  
  useEffect(() => {
    if (loading || !user) {
      setCheckingProfile(false);
      setProfileChecked(true);
      return;
    }

    const checkProfileCompletion = async () => {
      try {
        logger.debug('Checking profile completion for user:', user.id);
        
        const { data: profile, error } = await supabase
          .from('profiles')
          .select(`
            first_name,
            last_name,
            gender,
            birth_date,
            has_transport_service,
            emergency_contact_name,
            emergency_contact_number
          `)
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error in profile completion check:', error);
          toast.error("Kunde inte verifiera profilinformation. Fortsätter ändå.");
          setProfileChecked(true);
          setCheckingProfile(false);
          return;
        }

        const isMissingMandatoryFields = !profile?.first_name || 
                                      !profile?.last_name || 
                                      !profile?.gender || 
                                      !profile?.birth_date || 
                                      profile?.has_transport_service === null ||
                                      !profile?.emergency_contact_name ||
                                      !profile?.emergency_contact_number;

        logger.debug('Profile completion check result:', {
          hasProfile: !!profile,
          isMissingMandatoryFields
        });
        
        setProfileChecked(!isMissingMandatoryFields);
      } catch (error) {
        console.error('Error in profile completion check:', error);
        toast.error("Ett fel uppstod vid kontroll av profilinformation");
        setProfileChecked(true);
      } finally {
        setCheckingProfile(false);
      }
    };
    
    checkProfileCompletion();
  }, [user, loading, location.pathname]);

  if (checkError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-red-500 mb-4">
          Ett fel uppstod vid kontroll av inloggningsstatus.
        </div>
        <Button 
          onClick={() => window.location.reload()} 
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Ladda om sidan
        </Button>
      </div>
    );
  }

  if (loading || checkingProfile || (!sessionVerified && !user && !isLovableEditor)) {
    return <LoadingSpinner />;
  }

  if (!user && !isLovableEditor) {
    logger.debug('No authenticated user found and not in editor, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (user && !profileChecked && location.pathname !== '/profile') {
    logger.debug('Profile incomplete, redirecting to profile page');
    return <Navigate to="/profile?edit=true" replace />;
  }

  return <>{children}</>;
};

export default AuthenticationCheck;
